"use client";

/**
 * Full-screen "Choose artwork" picker over the whole app (Gordon 2026-11-03:
 * "you need to actually see the artwork"; "it can be full screen").
 *
 * - Real square previews with title + artist; the current cover marked.
 * - 1–5 pictures per row: buttons, PINCH on phones/tablets (spread = bigger),
 *   Ctrl/⌘ + wheel or trackpad pinch on computers. Remembered per device.
 * - z-[101] like the other full-screen layers, so the chat FAB (z-[105]) and
 *   coπ stay on top.
 * - Columns are an inline grid-template — the dialog safety rule turns
 *   .grid-cols-2 into one column on narrow phones, and a max-height on the
 *   grid itself squashed every row into a strip; the scroll box wraps it.
 */

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface VaultImage { url: string; title: string; artist: string }

const COLS_KEY = "bb-art-picker-cols";
const MIN = 1, MAX = 5;
const clamp = (n: number) => Math.max(MIN, Math.min(MAX, Math.round(n)));

function startCols(): number {
  try {
    const saved = Number(localStorage.getItem(COLS_KEY));
    if (saved >= MIN && saved <= MAX) return saved;
  } catch { /* storage blocked */ }
  if (typeof window === "undefined") return 3;
  return window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : 5;
}

export function VaultArtworkPicker({
  open, onOpenChange, loading, images, currentCover, onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  images: VaultImage[];
  currentCover: string;
  onPick: (url: string) => void;
}) {
  const t = useTranslations("music.artVault");
  const [query, setQuery] = useState("");
  const [cols, setColsState] = useState(3);
  const colsRef = useRef(3);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setCols = (n: number) => {
    const v = clamp(n);
    colsRef.current = v;
    setColsState(v);
    try { localStorage.setItem(COLS_KEY, String(v)); } catch { /* storage blocked */ }
  };

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const v = startCols();
    colsRef.current = v;
    setColsState(v);
  }, [open]);

  // Pinch (touch) and Ctrl/⌘ + wheel (trackpad pinch) change pictures per row.
  useEffect(() => {
    if (!open) return;
    let el: HTMLDivElement | null = null;
    let raf = 0;
    let pinch: { d0: number; c0: number } | null = null;
    let wheelAcc = 0;
    const dist = (e: TouchEvent) => Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) pinch = { d0: dist(e) || 1, c0: colsRef.current };
    };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pinch) return;
      e.preventDefault(); // our zoom, not the browser's
      const next = clamp(pinch.c0 / (dist(e) / pinch.d0));
      if (next !== colsRef.current) setCols(next);
    };
    const onEnd = (e: TouchEvent) => { if (e.touches.length < 2) pinch = null; };
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      wheelAcc += e.deltaY;
      if (Math.abs(wheelAcc) >= 40) {
        setCols(colsRef.current + (wheelAcc > 0 ? 1 : -1));
        wheelAcc = 0;
      }
    };
    // The dialog mounts its content a frame after `open`.
    const attach = () => {
      el = scrollRef.current;
      if (!el) { raf = requestAnimationFrame(attach); return; }
      el.addEventListener("touchstart", onStart, { passive: true });
      el.addEventListener("touchmove", onMove, { passive: false });
      el.addEventListener("touchend", onEnd);
      el.addEventListener("touchcancel", onEnd);
      el.addEventListener("wheel", onWheel, { passive: false });
    };
    attach();
    return () => {
      cancelAnimationFrame(raf);
      if (!el) return;
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
      el.removeEventListener("wheel", onWheel);
    };
  }, [open, loading, images.length]);

  const q = query.trim().toLowerCase();
  const shown = q ? images.filter(img => `${img.title} ${img.artist}`.toLowerCase().includes(q)) : images;
  const big = cols <= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="z-[101]"
        className="z-[101] !left-0 !top-0 !translate-x-0 !translate-y-0 h-[100dvh] !max-h-[100dvh] w-screen !max-w-none flex flex-col gap-3 rounded-none sm:rounded-none border-0 p-0 pt-[env(safe-area-inset-top)] !overflow-hidden data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0"
      >
        <DialogHeader className="px-page pt-4 pr-12 text-left">
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : images.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("empty")}</p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <div className="flex items-center gap-2 px-page">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search")}
                aria-label={t("search")}
                className="h-10 min-w-0 flex-1 rounded-md border-2 border-foreground/30 bg-background px-3 text-sm"
              />
              <div role="group" aria-label={t("perRow")} className="flex shrink-0 overflow-hidden rounded-md border-2 border-foreground/30">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCols(n)}
                    aria-pressed={cols === n}
                    aria-label={t("perRowN", { n })}
                    className={cn(
                      "h-9 w-8 text-sm font-semibold tabular-nums transition-colors",
                      cols === n ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <p className="px-page text-2xs text-muted-foreground">{t("pinchHint")}</p>
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("noMatch")}</p>
            ) : (
              <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-y-auto px-page pb-[calc(env(safe-area-inset-bottom)_+_6rem)]"
                style={{ touchAction: "pan-y" }}
              >
                <div className="grid gap-3 py-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                  {shown.map((img) => {
                    const isCurrent = img.url === currentCover;
                    return (
                      <button
                        key={img.url}
                        type="button"
                        className="group flex flex-col gap-1 text-left focus:outline-none"
                        aria-label={img.title || t("untitled")}
                        onClick={() => onPick(img.url)}
                      >
                        <span className={cn(
                          "relative block w-full aspect-square overflow-hidden rounded-[var(--thumbnail-radius)] border-2 bg-muted transition-colors",
                          isCurrent ? "border-primary" : "border-transparent group-hover:border-primary group-focus-visible:border-primary",
                        )}>
                          <Image
                            src={img.url}
                            alt=""
                            fill
                            sizes={`(max-width: 640px) ${Math.ceil(100 / cols)}vw, ${Math.ceil(1400 / cols)}px`}
                            className="object-cover"
                          />
                          {isCurrent && (
                            <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-3xs font-semibold text-primary-foreground">{t("current")}</span>
                          )}
                        </span>
                        <span className={cn("truncate font-medium", big ? "text-sm" : "text-xs")}>{img.title || t("untitled")}</span>
                        {img.artist && cols <= 4 && (
                          <span className={cn("-mt-1 truncate text-muted-foreground", big ? "text-xs" : "text-2xs")}>{img.artist}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
